import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { DEFAULT_LOCALE, LOCALES, translate } from './translations';

const I18nContext = createContext({
  locale: DEFAULT_LOCALE,
  locales: LOCALES,
  setLocale: () => {},
  t: (text) => text,
});

const ATTRIBUTE_NAMES = ['placeholder', 'title', 'aria-label', 'alt'];

const translateNode = (node, locale) => {
  if (!node || locale === 'en') return;

  if (node.nodeType === Node.TEXT_NODE) {
    const value = node.nodeValue;
    const trimmed = value.trim();
    if (!trimmed) return;

    const translated = translate(trimmed, locale);
    if (translated !== trimmed) {
      node.nodeValue = value.replace(trimmed, translated);
    }
    return;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return;

  ATTRIBUTE_NAMES.forEach((attributeName) => {
    const value = node.getAttribute(attributeName);
    if (!value) return;
    const translated = translate(value, locale);
    if (translated !== value) {
      node.setAttribute(attributeName, translated);
    }
  });

  node.childNodes.forEach((childNode) => translateNode(childNode, locale));
};

const useDomLocalization = (locale) => {
  useEffect(() => {
    document.documentElement.lang = locale;
    translateNode(document.body, locale);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => translateNode(node, locale));

        if (mutation.type === 'characterData') {
          translateNode(mutation.target, locale);
        }

        if (mutation.type === 'attributes') {
          translateNode(mutation.target, locale);
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ATTRIBUTE_NAMES,
    });

    return () => observer.disconnect();
  }, [locale]);
};

export const I18nProvider = ({ children }) => {
  const [locale, setLocaleState] = useState(() => localStorage.getItem('locale') || DEFAULT_LOCALE);

  const setLocale = (nextLocale) => {
    setLocaleState(nextLocale);
    localStorage.setItem('locale', nextLocale);
  };

  useDomLocalization(locale);

  const value = useMemo(() => ({
    locale,
    locales: LOCALES,
    setLocale,
    t: (text) => translate(text, locale),
  }), [locale]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => useContext(I18nContext);
